/**
 * Catálogos de la hoja de recepción (OS y cotizaciones): indicadores encendidos del
 * tablero y accesorios que se inventarían al recibir el coche.
 *
 * Cada taller los personaliza en Configuración › Recepción. En la OS se guardan por
 * NOMBRE (`[{ "Motor": true }]`), así que renombrar, desactivar o borrar una opción
 * no altera las órdenes ya capturadas: lo que la OS traiga fuera del catálogo vigente
 * se sigue mostrando como "extra".
 */

export interface TestigoConfig {
  nombre: string;
  /** Archivo de /assets/testigos/. null ⇒ ícono genérico de alerta. */
  icono: string | null;
  activo: boolean;
}

export interface AccesorioConfig {
  nombre: string;
  activo: boolean;
}

/** Íconos de tablero que trae el sistema; el taller elige uno para cada indicador. */
export const ICONOS_TESTIGO: { archivo: string; etiqueta: string }[] = [
  { archivo: 'freno-de-mano.png', etiqueta: 'Freno de mano' },
  { archivo: 'abs.png', etiqueta: 'ABS' },
  { archivo: 'aceite.png', etiqueta: 'Aceite' },
  { archivo: 'bateria.png', etiqueta: 'Batería' },
  { archivo: 'eps.png', etiqueta: 'Dirección (EPS)' },
  { archivo: 'motor.png', etiqueta: 'Motor' },
  { archivo: 'temperatura.png', etiqueta: 'Temperatura' },
  { archivo: 'gasolina.png', etiqueta: 'Gasolina' },
  { archivo: 'bolsas-de-aire.png', etiqueta: 'Bolsas de aire' },
  { archivo: 'cinturon.png', etiqueta: 'Cinturón' },
  { archivo: 'presion-baja.png', etiqueta: 'Presión de llantas' },
  { archivo: 'control-traccion.png', etiqueta: 'Control de tracción' },
];

export const TESTIGOS_DEFAULT: TestigoConfig[] = [
  { nombre: 'Freno de Mano', icono: 'freno-de-mano.png', activo: true },
  { nombre: 'ABS', icono: 'abs.png', activo: true },
  { nombre: 'Aceite', icono: 'aceite.png', activo: true },
  { nombre: 'Batería', icono: 'bateria.png', activo: true },
  { nombre: 'EPS', icono: 'eps.png', activo: true },
  { nombre: 'Motor', icono: 'motor.png', activo: true },
  { nombre: 'Temperatura', icono: 'temperatura.png', activo: true },
  { nombre: 'Gasolina', icono: 'gasolina.png', activo: true },
  { nombre: 'Bolsas de Aire', icono: 'bolsas-de-aire.png', activo: true },
  { nombre: 'Cinturón', icono: 'cinturon.png', activo: true },
  { nombre: 'Presión Baja', icono: 'presion-baja.png', activo: true },
  { nombre: 'Control de Tracción', icono: 'control-traccion.png', activo: true },
];

export const ACCESORIOS_DEFAULT: AccesorioConfig[] = [
  'Llanta de Refaccion', 'Gato Hidráulico', 'Cruceta', 'Autoestereo',
  'Cables pasa corriente', 'Extintor', 'Tapetes',
].map(nombre => ({ nombre, activo: true }));

type Marca = Record<string, boolean>;

/**
 * Lo que una OS/cotización trae fuera de la lista activa y debe seguir viéndose:
 * capturas manuales y opciones que el taller desactivó o renombró después, siempre
 * que estén marcadas (una opción desactivada y sin marcar no aporta nada).
 */
export function extrasFueraDeCatalogo(marcas: Marca[] | undefined, activos: string[], conocidos: string[]): string[] {
  const activosSet = new Set(activos);
  const conocidosSet = new Set(conocidos);
  return (marcas || [])
    .map(m => {
      const nombre = Object.keys(m)[0];
      return { nombre, valor: nombre ? m[nombre] : false };
    })
    .filter(({ nombre, valor }) => nombre && !activosSet.has(nombre) && (valor || !conocidosSet.has(nombre)))
    .map(({ nombre }) => nombre);
}
